import type { ComponentMaterial, PropertyPaneConfig } from "@shared/types";

import { buildEventBinderSection, buildMaterialPropertyPaneConfig, MOTION_PRESET_DEFAULT } from "./widgets/property-pane";

/**
 * 组件物料注册表（6.7 扩展版，共 41 个组件：18 现有 + 23 新增）。
 * 每个组件经由 materialize() 自动补齐：
 * - widgetClass：`${type}Widget`，经 widgets 注册表解析为 BaseWidget 子类
 * - propertyPaneConfig：由 propSchema 生成（主 section + 动效 section + 组件特有 section）
 * - registryUrl / swizzleEnabled：6.6 Registry 分发元数据
 * - defaultProps 统一携带 motionPreset（动效预设占位，动效引擎并行实现）
 */

const RAW_MATERIALS: ComponentMaterial[] = [
  {
    type: "Page",
    label: "页面",
    category: "layout",
    icon: "Layout",
    description: "页面根容器",
    allowChildren: true,
    defaultProps: {},
    propSchema: [
      { name: "title", label: "页面标题", type: "text", defaultValue: "新页面" },
      { name: "route", label: "路由", type: "text", defaultValue: "/new-page" },
    ],
  },
  {
    type: "Container",
    label: "容器",
    category: "layout",
    icon: "Square",
    description: "通用布局容器",
    allowChildren: true,
    defaultProps: { direction: "column", gap: "16px", padding: "16px" },
    propSchema: [
      {
        name: "direction",
        label: "排列方向",
        type: "select",
        options: ["row", "column"],
        defaultValue: "column",
      },
      { name: "gap", label: "间距", type: "text", defaultValue: "16px" },
      { name: "padding", label: "内边距", type: "text", defaultValue: "16px" },
      { name: "background", label: "背景色", type: "color" },
    ],
  },
  {
    type: "Text",
    label: "文本",
    category: "basic",
    icon: "Type",
    description: "纯文本/标题段落",
    defaultProps: { content: "这是一段文本", tag: "p", fontSize: "14px" },
    propSchema: [
      { name: "content", label: "内容", type: "dataBinding", defaultValue: "这是一段文本" },
      {
        name: "tag",
        label: "标签",
        type: "select",
        options: ["h1", "h2", "h3", "p", "span"],
        defaultValue: "p",
      },
      { name: "fontSize", label: "字体大小", type: "text", defaultValue: "14px" },
      { name: "color", label: "颜色", type: "color" },
      { name: "bold", label: "加粗", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "Button",
    label: "按钮",
    category: "basic",
    icon: "MousePointer",
    description: "可点击按钮",
    defaultProps: { label: "按钮", variant: "default", size: "default" },
    propSchema: [
      { name: "label", label: "文字", type: "dataBinding", defaultValue: "按钮" },
      {
        name: "variant",
        label: "样式",
        type: "select",
        options: ["default", "outline", "secondary", "ghost", "destructive"],
        defaultValue: "default",
      },
      {
        name: "size",
        label: "尺寸",
        type: "select",
        options: ["default", "sm", "lg", "icon"],
        defaultValue: "default",
      },
    ],
    eventSchema: [{ event: "onClick", label: "点击" }],
  },
  {
    type: "Input",
    label: "输入框",
    category: "basic",
    icon: "TextCursor",
    description: "单行文本输入",
    defaultProps: { placeholder: "请输入", label: "字段名", name: "", type: "text" },
    propSchema: [
      { name: "label", label: "标签", type: "text", defaultValue: "字段名" },
      { name: "name", label: "字段名", type: "text", placeholder: "例如 name", defaultValue: "" },
      { name: "placeholder", label: "占位提示", type: "text", defaultValue: "请输入" },
      {
        name: "type",
        label: "类型",
        type: "select",
        options: ["text", "password", "email", "number"],
        defaultValue: "text",
      },
      { name: "required", label: "必填", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "Textarea",
    label: "多行文本",
    category: "basic",
    icon: "AlignLeft",
    description: "多行文本输入",
    defaultProps: { placeholder: "请输入", label: "字段名", name: "", rows: 3 },
    propSchema: [
      { name: "label", label: "标签", type: "text", defaultValue: "字段名" },
      { name: "name", label: "字段名", type: "text", placeholder: "例如 description", defaultValue: "" },
      { name: "placeholder", label: "占位提示", type: "text", defaultValue: "请输入" },
      { name: "rows", label: "行数", type: "number", defaultValue: 3 },
      { name: "required", label: "必填", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "Select",
    label: "下拉选择",
    category: "basic",
    icon: "ListCollapse",
    description: "单选下拉框",
    defaultProps: { label: "字段名", name: "", placeholder: "请选择", options: [] },
    propSchema: [
      { name: "label", label: "标签", type: "text", defaultValue: "字段名" },
      { name: "name", label: "字段名", type: "text", placeholder: "例如 status", defaultValue: "" },
      { name: "placeholder", label: "占位提示", type: "text", defaultValue: "请选择" },
      { name: "options", label: "选项", type: "json", defaultValue: [{ label: "选项1", value: "1" }] },
      { name: "required", label: "必填", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "Switch",
    label: "开关",
    category: "basic",
    icon: "ToggleLeft",
    description: "布尔开关",
    defaultProps: { label: "字段名", name: "", required: false },
    propSchema: [
      { name: "label", label: "标签", type: "text", defaultValue: "字段名" },
      { name: "name", label: "字段名", type: "text", placeholder: "例如 active", defaultValue: "" },
      { name: "required", label: "必填", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "Checkbox",
    label: "复选框",
    category: "basic",
    icon: "CheckSquare",
    description: "布尔复选",
    defaultProps: { label: "字段名", name: "", required: false },
    propSchema: [
      { name: "label", label: "标签", type: "text", defaultValue: "字段名" },
      { name: "name", label: "字段名", type: "text", placeholder: "例如 agree", defaultValue: "" },
      { name: "required", label: "必填", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "Radio",
    label: "单选组",
    category: "basic",
    icon: "CircleDot",
    description: "单选按钮组",
    defaultProps: { label: "字段名", name: "", options: [] },
    propSchema: [
      { name: "label", label: "标签", type: "text", defaultValue: "字段名" },
      { name: "name", label: "字段名", type: "text", placeholder: "例如 gender", defaultValue: "" },
      { name: "options", label: "选项", type: "json", defaultValue: [{ label: "选项1", value: "1" }] },
      { name: "required", label: "必填", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "DatePicker",
    label: "日期选择",
    category: "basic",
    icon: "Calendar",
    description: "日期选择器",
    defaultProps: { label: "字段名", name: "", required: false },
    propSchema: [
      { name: "label", label: "标签", type: "text", defaultValue: "字段名" },
      { name: "name", label: "字段名", type: "text", placeholder: "例如 birthdate", defaultValue: "" },
      { name: "required", label: "必填", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "Table",
    label: "数据表格",
    category: "data",
    icon: "Table2",
    description: "展示数据列表",
    defaultProps: { resource: "", columns: [] },
    propSchema: [
      { name: "resource", label: "数据源表名", type: "text", placeholder: "例如 customers" },
      { name: "columns", label: "列配置", type: "json", defaultValue: [] },
      { name: "pageSize", label: "每页条数", type: "number", defaultValue: 10 },
    ],
  },
  {
    type: "Form",
    label: "表单",
    category: "data",
    icon: "FormInput",
    description: "数据提交表单容器",
    allowChildren: true,
    defaultProps: { resource: "", submitLabel: "提交" },
    propSchema: [
      { name: "resource", label: "提交表名", type: "text", placeholder: "例如 customers" },
      { name: "submitLabel", label: "提交按钮文字", type: "text", defaultValue: "提交" },
    ],
  },
  {
    type: "AIChat",
    label: "AI 对话",
    category: "ai",
    icon: "Bot",
    description: "AI 聊天组件",
    defaultProps: { welcomeMessage: "你好，有什么可以帮你的？", agentId: "" },
    propSchema: [
      {
        name: "welcomeMessage",
        label: "欢迎语",
        type: "textarea",
        defaultValue: "你好，有什么可以帮你的？",
      },
      { name: "agentId", label: "Agent ID", type: "text" },
      { name: "showRAG", label: "启用知识库", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "RichText",
    label: "富文本展示",
    category: "content",
    icon: "FileText",
    description: "渲染 HTML 富文本内容，可绑定 Strapi CMS 内容",
    defaultProps: { content: "<p>富文本内容</p>" },
    propSchema: [
      {
        name: "content",
        label: "HTML 内容",
        type: "textarea",
        defaultValue: "<p>富文本内容</p>",
      },
    ],
  },
  {
    type: "FileUpload",
    label: "文件上传",
    category: "content",
    icon: "Upload",
    description: "上传文件到知识库或文件存储",
    defaultProps: { target: "knowledge", accept: ".txt,.md" },
    propSchema: [
      {
        name: "target",
        label: "上传目标",
        type: "select",
        options: ["knowledge", "storage"],
        defaultValue: "knowledge",
      },
      { name: "accept", label: "接受文件类型", type: "text", defaultValue: ".txt,.md" },
    ],
  },
  {
    type: "Chart",
    label: "图表",
    category: "chart",
    icon: "BarChart",
    description: "数据可视化（通用图表）",
    defaultProps: { type: "bar", resource: "", xField: "", yField: "" },
    propSchema: [
      {
        name: "type",
        label: "图表类型",
        type: "select",
        options: ["bar", "line", "pie"],
        defaultValue: "bar",
      },
      { name: "resource", label: "数据源表名", type: "text" },
      { name: "xField", label: "X 轴字段", type: "text" },
      { name: "yField", label: "Y 轴字段", type: "text" },
    ],
  },
  {
    type: "Card",
    label: "卡片",
    category: "layout",
    icon: "CreditCard",
    description: "信息卡片容器",
    allowChildren: true,
    defaultProps: { title: "卡片标题" },
    propSchema: [{ name: "title", label: "标题", type: "dataBinding", defaultValue: "卡片标题" }],
  },

  // ------------------------------------------------------------
  // 6.7 扩展：layout
  // ------------------------------------------------------------
  {
    type: "Grid",
    label: "栅格",
    category: "layout",
    icon: "LayoutGrid",
    description: "CSS Grid 栅格布局容器",
    allowChildren: true,
    defaultProps: { columns: 3, gap: "16px", padding: "16px" },
    propSchema: [
      { name: "columns", label: "列数", type: "number", defaultValue: 3 },
      { name: "gap", label: "间距", type: "text", defaultValue: "16px" },
      { name: "padding", label: "内边距", type: "text", defaultValue: "16px" },
    ],
  },
  {
    type: "Flex",
    label: "弹性布局",
    category: "layout",
    icon: "Rows3",
    description: "Flexbox 弹性布局容器",
    allowChildren: true,
    defaultProps: { direction: "row", justify: "flex-start", align: "stretch", gap: "12px", wrap: false },
    propSchema: [
      {
        name: "direction",
        label: "方向",
        type: "select",
        options: ["row", "column"],
        defaultValue: "row",
      },
      {
        name: "justify",
        label: "主轴对齐",
        type: "select",
        options: ["flex-start", "center", "flex-end", "space-between", "space-around"],
        defaultValue: "flex-start",
      },
      {
        name: "align",
        label: "交叉轴对齐",
        type: "select",
        options: ["stretch", "flex-start", "center", "flex-end"],
        defaultValue: "stretch",
      },
      { name: "gap", label: "间距", type: "text", defaultValue: "12px" },
      { name: "wrap", label: "换行", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "Stack",
    label: "堆叠布局",
    category: "layout",
    icon: "Layers",
    description: "垂直/水平堆叠布局（简化 Flex）",
    allowChildren: true,
    defaultProps: { direction: "column", spacing: "16px", align: "stretch" },
    propSchema: [
      {
        name: "direction",
        label: "方向",
        type: "select",
        options: ["column", "row"],
        defaultValue: "column",
      },
      { name: "spacing", label: "间距", type: "text", defaultValue: "16px" },
      {
        name: "align",
        label: "对齐",
        type: "select",
        options: ["stretch", "flex-start", "center", "flex-end"],
        defaultValue: "stretch",
      },
    ],
  },
  {
    type: "Tabs",
    label: "标签页",
    category: "layout",
    icon: "PanelsTopLeft",
    description: "多标签页容器",
    allowChildren: true,
    defaultProps: {
      tabs: [
        { label: "标签1", value: "tab1" },
        { label: "标签2", value: "tab2" },
      ],
      defaultValue: "tab1",
    },
    propSchema: [
      {
        name: "tabs",
        label: "标签配置",
        type: "json",
        defaultValue: [
          { label: "标签1", value: "tab1" },
          { label: "标签2", value: "tab2" },
        ],
      },
      { name: "defaultValue", label: "默认选中", type: "text", defaultValue: "tab1" },
    ],
  },
  {
    type: "Sidebar",
    label: "侧边栏",
    category: "layout",
    icon: "PanelLeft",
    description: "导航侧边栏",
    allowChildren: true,
    defaultProps: {
      title: "导航",
      items: [
        { label: "首页", path: "/" },
        { label: "设置", path: "/settings" },
      ],
      collapsed: false,
    },
    propSchema: [
      { name: "title", label: "标题", type: "text", defaultValue: "导航" },
      {
        name: "items",
        label: "导航项",
        type: "json",
        defaultValue: [
          { label: "首页", path: "/" },
          { label: "设置", path: "/settings" },
        ],
      },
      { name: "collapsed", label: "折叠", type: "boolean", defaultValue: false },
    ],
  },

  // ------------------------------------------------------------
  // 6.7 扩展：basic
  // ------------------------------------------------------------
  {
    type: "Slider",
    label: "滑杆",
    category: "basic",
    icon: "SlidersHorizontal",
    description: "数值滑杆选择器",
    defaultProps: { label: "滑杆", min: 0, max: 100, step: 1, defaultValue: 50, name: "" },
    propSchema: [
      { name: "label", label: "标签", type: "text", defaultValue: "滑杆" },
      { name: "min", label: "最小值", type: "number", defaultValue: 0 },
      { name: "max", label: "最大值", type: "number", defaultValue: 100 },
      { name: "step", label: "步长", type: "number", defaultValue: 1 },
      { name: "defaultValue", label: "默认值", type: "number", defaultValue: 50 },
      { name: "name", label: "字段名", type: "text", defaultValue: "" },
    ],
  },

  // ------------------------------------------------------------
  // 6.7 扩展：data
  // ------------------------------------------------------------
  {
    type: "List",
    label: "数据列表",
    category: "data",
    icon: "List",
    description: "列表展示数据行",
    defaultProps: { resource: "", field: "name", limit: 10, header: "数据列表" },
    propSchema: [
      { name: "resource", label: "数据源表名", type: "text", placeholder: "例如 customers" },
      { name: "field", label: "展示字段", type: "text", defaultValue: "name" },
      { name: "limit", label: "显示条数", type: "number", defaultValue: 10 },
      { name: "header", label: "标题", type: "text", defaultValue: "数据列表" },
    ],
  },
  {
    type: "Pagination",
    label: "分页",
    category: "data",
    icon: "ChevronsRight",
    description: "数据分页控件",
    defaultProps: { resource: "", pageSize: 10, current: 1, total: 0 },
    propSchema: [
      { name: "resource", label: "数据源表名", type: "text" },
      { name: "pageSize", label: "每页条数", type: "number", defaultValue: 10 },
      { name: "current", label: "当前页", type: "number", defaultValue: 1 },
      { name: "total", label: "总条数", type: "number", defaultValue: 0 },
    ],
  },
  {
    type: "SearchBar",
    label: "搜索框",
    category: "data",
    icon: "Search",
    description: "数据搜索输入框",
    defaultProps: { placeholder: "搜索...", resource: "", field: "", value: "" },
    propSchema: [
      { name: "placeholder", label: "占位提示", type: "text", defaultValue: "搜索..." },
      { name: "resource", label: "数据源表名", type: "text" },
      { name: "field", label: "搜索字段", type: "text" },
      { name: "value", label: "值", type: "dataBinding", defaultValue: "" },
    ],
  },
  {
    type: "FilterPanel",
    label: "筛选面板",
    category: "data",
    icon: "Filter",
    description: "多条件筛选面板",
    defaultProps: {
      resource: "",
      fields: [{ name: "status", label: "状态", type: "select", options: ["active", "inactive"] }],
    },
    propSchema: [
      { name: "resource", label: "数据源表名", type: "text" },
      {
        name: "fields",
        label: "筛选字段",
        type: "json",
        defaultValue: [
          { name: "status", label: "状态", type: "select", options: ["active", "inactive"] },
        ],
      },
    ],
  },

  // ------------------------------------------------------------
  // 6.7 扩展：ai
  // ------------------------------------------------------------
  {
    type: "AICopilot",
    label: "AI 助手",
    category: "ai",
    icon: "Sparkles",
    description: "内嵌 AI 问答助手",
    defaultProps: { placeholder: "输入问题，AI 助手为你解答...", agentId: "" },
    propSchema: [
      { name: "placeholder", label: "占位提示", type: "text", defaultValue: "输入问题，AI 助手为你解答..." },
      { name: "agentId", label: "Agent ID", type: "text" },
    ],
  },
  {
    type: "AIGenerate",
    label: "AI 生成",
    category: "ai",
    icon: "Wand2",
    description: "AI 生成内容",
    defaultProps: { prompt: "生成一份客户管理页面", agentId: "" },
    propSchema: [
      { name: "prompt", label: "生成提示", type: "textarea", defaultValue: "生成一份客户管理页面" },
      { name: "agentId", label: "Agent ID", type: "text" },
    ],
  },

  // ------------------------------------------------------------
  // 6.7 扩展：chart（自包含 SVG 渲染，不引入图表库）
  // ------------------------------------------------------------
  {
    type: "BarChart",
    label: "柱状图",
    category: "chart",
    icon: "BarChart3",
    description: "柱状图（SVG 自渲染）",
    defaultProps: { resource: "", xField: "name", yField: "value", height: 240, color: "#0ea5e9" },
    propSchema: [
      { name: "resource", label: "数据源表名", type: "text" },
      { name: "xField", label: "X 轴字段", type: "text", defaultValue: "name" },
      { name: "yField", label: "Y 轴字段", type: "text", defaultValue: "value" },
      { name: "height", label: "高度", type: "number", defaultValue: 240 },
      { name: "color", label: "颜色", type: "color", defaultValue: "#0ea5e9" },
    ],
  },
  {
    type: "LineChart",
    label: "折线图",
    category: "chart",
    icon: "ChartLine",
    description: "折线图（SVG 自渲染）",
    defaultProps: { resource: "", xField: "name", yField: "value", height: 240, color: "#0ea5e9" },
    propSchema: [
      { name: "resource", label: "数据源表名", type: "text" },
      { name: "xField", label: "X 轴字段", type: "text", defaultValue: "name" },
      { name: "yField", label: "Y 轴字段", type: "text", defaultValue: "value" },
      { name: "height", label: "高度", type: "number", defaultValue: 240 },
      { name: "color", label: "颜色", type: "color", defaultValue: "#0ea5e9" },
    ],
  },
  {
    type: "PieChart",
    label: "饼图",
    category: "chart",
    icon: "PieChart",
    description: "饼图（SVG 自渲染）",
    defaultProps: { resource: "", labelField: "name", valueField: "value", height: 240 },
    propSchema: [
      { name: "resource", label: "数据源表名", type: "text" },
      { name: "labelField", label: "分类字段", type: "text", defaultValue: "name" },
      { name: "valueField", label: "数值字段", type: "text", defaultValue: "value" },
      { name: "height", label: "高度", type: "number", defaultValue: 240 },
    ],
  },
  {
    type: "AreaChart",
    label: "面积图",
    category: "chart",
    icon: "ChartArea",
    description: "面积图（SVG 自渲染）",
    defaultProps: { resource: "", xField: "name", yField: "value", height: 240, color: "#0ea5e9" },
    propSchema: [
      { name: "resource", label: "数据源表名", type: "text" },
      { name: "xField", label: "X 轴字段", type: "text", defaultValue: "name" },
      { name: "yField", label: "Y 轴字段", type: "text", defaultValue: "value" },
      { name: "height", label: "高度", type: "number", defaultValue: 240 },
      { name: "color", label: "颜色", type: "color", defaultValue: "#0ea5e9" },
    ],
  },
  {
    type: "MetricCard",
    label: "指标卡",
    category: "chart",
    icon: "Gauge",
    description: "数据指标卡片",
    defaultProps: { label: "指标", resource: "", field: "value", aggregate: "sum", prefix: "", suffix: "", color: "#0f172a" },
    propSchema: [
      { name: "label", label: "指标名", type: "text", defaultValue: "指标" },
      { name: "resource", label: "数据源表名", type: "text" },
      { name: "field", label: "数值字段", type: "text", defaultValue: "value" },
      {
        name: "aggregate",
        label: "聚合方式",
        type: "select",
        options: ["sum", "avg", "count", "max", "min", "last"],
        defaultValue: "sum",
      },
      { name: "prefix", label: "前缀", type: "text", defaultValue: "" },
      { name: "suffix", label: "后缀", type: "text", defaultValue: "" },
      { name: "color", label: "颜色", type: "color", defaultValue: "#0f172a" },
    ],
  },

  // ------------------------------------------------------------
  // 6.7 扩展：content
  // ------------------------------------------------------------
  {
    type: "Image",
    label: "图片",
    category: "content",
    icon: "Image",
    description: "展示图片",
    defaultProps: { src: "", alt: "图片", objectFit: "cover", width: "100%", height: "auto", rounded: false },
    propSchema: [
      { name: "src", label: "图片地址", type: "dataBinding", defaultValue: "" },
      { name: "alt", label: "替代文本", type: "text", defaultValue: "图片" },
      {
        name: "objectFit",
        label: "填充方式",
        type: "select",
        options: ["cover", "contain", "fill"],
        defaultValue: "cover",
      },
      { name: "width", label: "宽度", type: "text", defaultValue: "100%" },
      { name: "height", label: "高度", type: "text", defaultValue: "auto" },
      { name: "rounded", label: "圆角", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "Video",
    label: "视频",
    category: "content",
    icon: "Video",
    description: "展示视频",
    defaultProps: { src: "", poster: "", controls: true, autoplay: false, muted: false },
    propSchema: [
      { name: "src", label: "视频地址", type: "dataBinding", defaultValue: "" },
      { name: "poster", label: "封面图", type: "text", defaultValue: "" },
      { name: "controls", label: "显示控件", type: "boolean", defaultValue: true },
      { name: "autoplay", label: "自动播放", type: "boolean", defaultValue: false },
      { name: "muted", label: "静音", type: "boolean", defaultValue: false },
    ],
  },
  {
    type: "Markdown",
    label: "Markdown",
    category: "content",
    icon: "FileCode",
    description: "渲染 Markdown 内容（轻量自写解析器）",
    defaultProps: { content: "# 标题\n\n段落内容" },
    propSchema: [
      { name: "content", label: "Markdown 内容", type: "textarea", defaultValue: "# 标题\n\n段落内容" },
    ],
  },

  // ------------------------------------------------------------
  // 6.7 扩展：motion（自包含 CSS 动效，接口预留给并行动效引擎）
  // ------------------------------------------------------------
  {
    type: "MotionGroup",
    label: "动效组",
    category: "motion",
    icon: "Move",
    description: "子元素交错动效容器",
    allowChildren: true,
    defaultProps: { animation: "fade-in", duration: 400, delay: 0, stagger: 80 },
    propSchema: [
      {
        name: "animation",
        label: "动效",
        type: "select",
        options: ["fade-in", "slide-up", "slide-in-left", "scale-in", "none"],
        defaultValue: "fade-in",
      },
      { name: "duration", label: "时长(ms)", type: "number", defaultValue: 400 },
      { name: "delay", label: "延迟(ms)", type: "number", defaultValue: 0 },
      { name: "stagger", label: "交错间隔(ms)", type: "number", defaultValue: 80 },
    ],
  },
  {
    type: "AnimatedRoute",
    label: "路由动画",
    category: "motion",
    icon: "Route",
    description: "路由切换进出场动画容器",
    allowChildren: true,
    defaultProps: { animation: "slide-up", duration: 350, direction: "up" },
    propSchema: [
      {
        name: "animation",
        label: "动效",
        type: "select",
        options: ["fade-in", "slide-up", "slide-in-left", "scale-in", "none"],
        defaultValue: "slide-up",
      },
      { name: "duration", label: "时长(ms)", type: "number", defaultValue: 350 },
      {
        name: "direction",
        label: "方向",
        type: "select",
        options: ["up", "down", "left", "right"],
        defaultValue: "up",
      },
    ],
  },
  {
    type: "ScrollReveal",
    label: "滚动显现",
    category: "motion",
    icon: "Eye",
    description: "滚动进入视口时显现",
    allowChildren: true,
    defaultProps: { animation: "slide-up", delay: 0, threshold: 0.2, once: true },
    propSchema: [
      {
        name: "animation",
        label: "动效",
        type: "select",
        options: ["fade-in", "slide-up", "scale-in", "none"],
        defaultValue: "slide-up",
      },
      { name: "delay", label: "延迟(ms)", type: "number", defaultValue: 0 },
      { name: "threshold", label: "显现阈值", type: "number", defaultValue: 0.2 },
      { name: "once", label: "仅一次", type: "boolean", defaultValue: true },
    ],
  },
];

/** 组件特有属性面板 section（除默认"内容 + 动效"外） */
const EXTRA_PANE_SECTIONS: Record<string, PropertyPaneConfig[]> = {
  Button: [buildEventBinderSection([{ event: "onClick", label: "点击" }])],
};

const materialize = (m: ComponentMaterial): ComponentMaterial => ({
  ...m,
  defaultProps: { motionPreset: MOTION_PRESET_DEFAULT, ...m.defaultProps },
  widgetClass: `${m.type}Widget`,
  registryUrl: `/registry/${m.category}/${m.type}.json`,
  swizzleEnabled: true,
  propertyPaneConfig: buildMaterialPropertyPaneConfig(m.propSchema, EXTRA_PANE_SECTIONS[m.type]),
});

export const componentMaterials: ComponentMaterial[] = RAW_MATERIALS.map(materialize);

export const materialMap = new Map(componentMaterials.map((m) => [m.type, m]));

export function getMaterial(type: string): ComponentMaterial | undefined {
  return materialMap.get(type);
}
